/**
 * Typed access to Vite env. All runtime config lives in .env files.
 *
 * Adding a new flag: add it here, then to .env.example.
 */

const raw = import.meta.env as Record<string, string | undefined>;

function str(key: string, fallback = ''): string {
  const v = raw[key];
  return v == null || v === '' ? fallback : v;
}

function bool(key: string, fallback = false): boolean {
  const v = raw[key];
  if (v == null || v === '') return fallback;
  return v === 'true' || v === '1' || v === 'yes';
}

function num(key: string, fallback: number): number {
  const v = raw[key];
  if (v == null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export interface AppEnv {
  smsApiUrl: string;
  tariffsApiUrl: string;
  cdpApiUrl: string;
  cdpUseStub: boolean;
  /** Tracker / Tracking Service API base (or /api/tracker via Vite proxy in dev). */
  trackerApiUrl: string;
  /** Auth service base URL (or /api/auth via Vite proxy in dev). */
  authApiUrl: string;
  /** Portal app for /auth/login (session refresh). */
  authAppName: string;
  /**
   * Registered auth application names (= JWT aud) per backend API.
   * Tokens obtained via POST /auth/token after login.
   */
  apiAuthApps: {
    sms: string;
    tariffs: string;
    cdp: string;
    tracker: string;
    /** Auth-service admin API (`GET /users`, …) — aud is usually `botto-auth`. */
    auth: string;
  };
  authEnabled: boolean;
  defaultPageSize: number;
  devextremeTheme: string;
  devextremeLicenseKey: string;
  isDev: boolean;
  isProd: boolean;
}

export const env: AppEnv = {
  // In dev we want browser requests to go through Vite dev-server proxy
  // to avoid CORS. `apps/shell/vite.config.ts` exposes `/api/sms`, `/api/billing`,
  // `/api/cdp`, `/api/tracker` when corresponding `VITE_PROXY_*_TARGET` variables are set.
  smsApiUrl:
    !!import.meta.env.DEV && raw.VITE_PROXY_SMS_TARGET
      ? '/api/sms'
      : str('VITE_SMS_API_URL', 'https://sms.bot-to.ru/api/sms'),
  tariffsApiUrl:
    !!import.meta.env.DEV && raw.VITE_PROXY_TARIFFS_TARGET
      ? '/api/billing'
      : str('VITE_TARIFFS_API_URL', 'https://sms.bot-to.ru/api/billing'),
  cdpApiUrl:
    !!import.meta.env.DEV && raw.VITE_PROXY_CDP_TARGET
      ? '/api/cdp'
      : str('VITE_CDP_API_URL', ''),
  cdpUseStub: bool('VITE_CDP_USE_STUB', false),
  trackerApiUrl:
    !!import.meta.env.DEV && raw.VITE_PROXY_TRACKER_TARGET
      ? '/api/tracker'
      : str('VITE_TRACKER_API_URL', ''),
  authApiUrl:
    !!import.meta.env.DEV && raw.VITE_PROXY_AUTH_TARGET
      ? // Same path shape as prod (`/api` + `/auth/...`); Vite proxies `/api/auth`.
        '/api'
      : str('VITE_AUTH_API_URL', '/api'),
  authAppName: str('VITE_AUTH_APP_NAME', 'sms-api'),
  apiAuthApps: {
    sms: str('VITE_SMS_AUTH_APP_NAME', 'sms-api'),
    tariffs: str('VITE_TARIFFS_AUTH_APP_NAME', 'billing-api'),
    cdp: str('VITE_CDP_AUTH_APP_NAME', 'cdp-api'),
    tracker: str('VITE_TRACKER_AUTH_APP_NAME', 'tracker-api'),
    auth: str('VITE_AUTH_SERVICE_APP_NAME', 'botto-auth'),
  },
  authEnabled: bool('VITE_AUTH_ENABLED', true),
  defaultPageSize: num('VITE_DEFAULT_PAGE_SIZE', 20),
  devextremeTheme: str('VITE_DEVEXTREME_THEME', 'material.blue.light'),
  devextremeLicenseKey: str('VITE_DEVEXTREME_LICENSE_KEY', ''),
  isDev: !!import.meta.env.DEV,
  isProd: !!import.meta.env.PROD,
};
